import { CompetitionTeamService } from './../competition-team.service'
import { competitionWhitelistSelectors } from './../../competition-whitelist/competition-whitelist.selectors'
import { competitionWhitelistActions } from './../../competition-whitelist/competition-whitelist.actions'
import { Component, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Observable, of, Subscription } from 'rxjs'
import {
  take,
  filter,
  map,
  timeout,
  catchError,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'

import { ConfigurationService } from 'src/app/shared/services/configuration.service'
import { competitionBlacklistActions } from '../../competition-blacklist/competition-blacklist.actions'
import { competitionPlayerSelectors } from '../../competition-player/competition-player.selectors'
import { competitionSportSelectors } from '../../competition-sport/competition-sport.selectors'
import { competitionSelectors } from '../../competition.selectors'
import { CompetitionTeam } from '../competition-team'
import { competitionTeamActions } from '../competition-team.actions'
import { competitionTeamSelectors } from '../competition-team.selectors'
import { CompetitionTeamCreateFormComponent } from '../create-form/show.component'
import { TranslateService } from '@ngx-translate/core'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Competition } from '../../competition'
import { persistentUserRoleSelectors } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.selector'
import { environment } from 'src/environments/environment'
import { competitionActions } from '../../competition.actions'
import { competitionMatchActions } from '../../competition-match/competition-match.actions'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { AppRoutesNS } from 'src/app/app-routes.namespace'

@UntilDestroy()
@Component({
  selector: 'competition-create-team',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionTeamCreateComponent implements OnInit {
  public isLoading$: Observable<boolean>
  public activeTab: string = 'teamDetails'
  public teamId$: Observable<number>
  public team$: Observable<CompetitionTeam>
  public minPlayers: number
  public maxPlayers: number
  public invitedPlayers$: Observable<any[]>
  public invitedStaffMembers$: Observable<any[]>
  private teamId: number

  @ViewChild(CompetitionTeamCreateFormComponent, { static: false })
  private teamCreateFormRef: CompetitionTeamCreateFormComponent
  public selectWhiteList$: Observable<any>
  public selectBlackList$: Observable<any>
  public competition$: Observable<Competition>
  public appRoutesNS = AppRoutesNS
  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private notificationService: NotificationService,
    private router: Router,
    public configurationService: ConfigurationService,
    private translateService: TranslateService,
    private competitionTeamService: CompetitionTeamService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.store.dispatch(
      BreadCrumbActions.addPageToHistory({
        pageName: 'general.back',
        url: AppRoutesNS.Competitions.COMPETITIONS,
        showBackArrow: true,
        titleName: 'competitionModule.competitions'
      })
    )
    this.route.queryParamMap.pipe(untilDestroyed(this)).subscribe((params) => {
      const activeTab = params.get('activeTab')
      this.activeTab = activeTab || 'teamDetails'
    })
    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )
    this.teamId$
      .pipe(
        take(1),
        filter((id) => id !== 0 && id !== null)
      )
      .subscribe((id) => {
        this.teamId = id
        return this.store.dispatch(
          competitionTeamActions.fetchOneById({
            id,
            params: RequestQueryBuilder.create({
              join: [{ field: 'competitions' }]
            })
          })
        )
      })

    this.team$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionTeamSelectors.selectById, teamId)
      )
    )

    this.competition$ = this.team$.pipe(
      switchMap((team) =>
        this.store.select(competitionSelectors.selectById, team.competitionId)
      )
    )

    this.invitedPlayers$ = this.store.select(
      competitionTeamSelectors.selectInvitedPlayers
    )

    this.invitedStaffMembers$ = this.store.select(
      competitionTeamSelectors.selectInvitedStaffMembers
    )
  }

  public onClickNextButton() {
    let competition

    this.competition$.pipe(take(1)).subscribe((item) => (competition = item))

    if (this.activeTab == 'planning') {
      let errorMessage = null

      this.store
        .select(competitionWhitelistSelectors.selectByFilter, {
          field: 'competitionTeamId',
          value: this.teamId
        })
        .pipe(take(1))
        .subscribe((whiteListItemsOfTeam) => {
          if (competition?.minWhitelist > whiteListItemsOfTeam?.length) {
            this.translateService
              .get('alerts.selectMinimumWhiteList')
              .pipe(take(1))
              .subscribe((minWhiteListText) => {
                errorMessage = minWhiteListText.replace(
                  '{$MIN_WHITELIST_ITEMS}',
                  competition.minWhitelist
                )
              })
          }
        })

      if (errorMessage) {
        return this.notificationService.emit(errorMessage, 'error')
      }
    }

    switch (this.activeTab) {
      case 'teamDetails':
        this.teamCreateFormRef.attemptSubmit()
        break
      case 'planning':
        this.team$.pipe(take(1)).subscribe((team) =>
          this.router.navigate([`/competitions/${team.id}/create`], {
            queryParams: { activeTab: 'members' }
          })
        )
        break
      case 'members':
        let teamId
        let subscription: Subscription
        this.team$.pipe(take(1)).subscribe((team) => {
          teamId = team.id
        })

        let competitionPlayer$: Observable<any> = this.team$.pipe(
          untilDestroyed(this),
          switchMap((team) =>
            this.store.select(
              competitionSelectors.selectById,
              team?.competitionId
            )
          ),
          switchMap((competition) =>
            this.store.select(
              competitionSportSelectors.selectById,
              competition?.competitionSportId
            )
          ),
          tap((competitionSport: any) => {
            this.minPlayers = competitionSport?.teamMinPlayers
            this.maxPlayers = competitionSport?.teamMaxPlayers
          }),
          switchMap((competitionSport) =>
            this.store.select(competitionPlayerSelectors.selectByFilter, {
              field: 'competitionTeamId',
              value: teamId
            })
          )
        )
        subscription = competitionPlayer$
          .pipe(
            take(1),
            untilDestroyed(this),
            withLatestFrom(this.invitedPlayers$)
          )
          .subscribe(([players, invitedPlayers]) => {
            if (!players) {
              players = []
            }
            if (!invitedPlayers) {
              invitedPlayers = []
            }

            const invitedPlayersOfTeam = invitedPlayers.filter(
              (player) => player.teamId == teamId
            )

            if (
              [...players, ...invitedPlayersOfTeam].length < this.minPlayers
            ) {
              this.translateService
                .get('competitionModule.minPlayers')
                .pipe(take(1))
                .subscribe((text) => {
                  this.notificationService.emit(text, 'error')
                })
              return
            }

            if (
              [...players, ...invitedPlayersOfTeam].length > this.maxPlayers
            ) {
              this.translateService
                .get('competitionModule.maxPlayers')
                .pipe(take(1))
                .subscribe((text) => {
                  this.notificationService.emit(text, 'error')
                })
              return
            }
            this.saveCompetition()
          })
        subscription.unsubscribe()
        break

      default:
        break
    }
  }

  private saveCompetition() {
    this.invitedPlayers$
      .pipe(
        timeout(5000),
        filter((x) => Boolean(x)),
        take(1),
        withLatestFrom(
          this.invitedStaffMembers$,
          this.store.select(settingSelectors.selectById, 'language'),
          this.team$
        )
      )
      .subscribe(
        ([invitedPlayers, invitedStaffMembers, selectedLanguage, team]) => {
          const templateType = 'Competition_team_registration_invite'

          invitedPlayers?.map((invitedPlayer) => {
            this.competitionTeamService
              .sendInvitationEmail(
                team,
                templateType,
                selectedLanguage,
                invitedPlayer.email,
                'player',
                invitedPlayer.functionId,
                team?.competitionId,
                invitedPlayer.number
              )
              .subscribe()
          })

          invitedStaffMembers?.map((invitedStaff) => {
            this.competitionTeamService
              .sendInvitationEmail(
                team,
                templateType,
                selectedLanguage,
                invitedStaff.email,
                'staff',
                invitedStaff.functionId,
                team?.competitionId,
                invitedStaff.number
              )
              .subscribe()
          })

          this.store.dispatch(
            competitionTeamActions.setInvitedStaffMembers({
              entity: []
            })
          )
          this.store.dispatch(
            competitionTeamActions.setInvitedPlayers({
              entity: []
            })
          )

          this.notificationService.emit('alerts.success', 'success')

          return this.router.navigate([`/competition-team/${team.id}`])
        }
      )
  }
  public onClickBackButton() {
    switch (this.activeTab) {
      case 'planning':
        this.team$.pipe(take(1)).subscribe((team) => {
          this.router.navigate([`/competitions/${team.id}/create`], {
            queryParams: { activeTab: 'teamDetails' }
          })
        })
        break
      case 'members':
        this.team$.pipe(take(1)).subscribe((team) => {
          this.router.navigate([`/competitions/${team.id}/create`], {
            queryParams: { activeTab: 'planning' }
          })
        })
        break
      default:
        break
    }
  }

  public onTeamDetailsFormSubmit(team: Partial<CompetitionTeam>) {
    if (team?.id) {
      this.store.dispatch(
        competitionTeamActions.updateOneById({
          id: team.id,
          params: { ...team }
        })
      )
    } else {
      this.store
        .select(persistentUserRoleSelectors.selectSelectedUserRole)
        .pipe(take(1))
        .subscribe((pUr) => {
          const siteId =
            pUr && pUr.siteId ? pUr.siteId : environment.defaultSiteId
          this.store.dispatch(
            competitionTeamActions.createOne({
              entity: {
                ...team,
                siteId: siteId,
                competitionPreviousTeamId: 0
              }
            })
          )
        })
    }

    this.actions$
      .pipe(
        ofType(competitionTeamActions.upsertMany),
        take(1),
        filter((action) => Boolean(action.entities?.length)),
        map((action) => action.entities[0]),
        timeout(2000),
        catchError(() => of(null))
      )
      .subscribe((team: CompetitionTeam) => {
        if (!team) {
          return this.notificationService.emit('alerts.error', 'error')
        }
        this.router.navigate([`/competitions/${team.id}/create`], {
          queryParams: { activeTab: 'planning' }
        })

        this.notificationService.emit('alerts.success', 'success')
      })
  }

  public onPlanningTabSubmit(formArray: any[]): void {
    formArray?.forEach((form) => {
      if (form?.type == 'whitelist') {
        this.store.dispatch(
          competitionWhitelistActions.setCurrentWhiteList({
            currentWhiteListSelected: form
          })
        )
        this.store
          .select(persistentUserRoleSelectors.selectSelectedUserRole)
          .pipe(take(1))
          .subscribe((pUr) => {
            const siteId =
              pUr && pUr.siteId ? pUr.siteId : environment.defaultSiteId
            this.store.dispatch(
              competitionWhitelistActions.createOne({
                entity: {
                  siteId: siteId,
                  ...form
                }
              })
            )
          })
      }
      if (form?.type == 'blacklist') {
        this.store.dispatch(
          competitionBlacklistActions.setCurrentBlackList({
            currentBlackListSelected: form
          })
        )
        this.store
          .select(persistentUserRoleSelectors.selectSelectedUserRole)
          .pipe(take(1))
          .subscribe((pUr) => {
            const siteId =
              pUr && pUr.siteId ? pUr.siteId : environment.defaultSiteId
            this.store.dispatch(
              competitionBlacklistActions.createOne({
                entity: {
                  siteId: siteId,
                  ...form
                }
              })
            )
          })
      }
    })

    this.actions$
      .pipe(
        ofType(competitionTeamActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => {
          console.log(error)
          return of(null)
        })
      )
      .subscribe((action) => {
        if (action) {
          if (action.entities[0].competitionId) {
            this.store.dispatch(
              competitionActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: { id: action.entities[0].competitionId }
                })
              })
            )
            this.store.dispatch(
              competitionMatchActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: { competitionId: action.entities[0].competitionId },
                  join: [{ field: 'booking' }]
                })
              })
            )
          }

          this.notificationService.emit('alerts.success', 'success')
        }
      })
  }
}
